/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  WHAT KIND OF ACCOUNT IS SIGNED IN
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * One definition, because there were twenty.
 *
 * `['studio', 'manager', 'instructor'].includes(admin?.role)` was written out
 * by hand in twenty components, and `role === 'studio'` in eighteen more. When
 * `studio_owner` and `brand_owner` were added to StudioUser, every one of
 * those had to be found — and a missed one does not throw. It classifies a
 * brand owner as an ADMIN, sends them down admin code paths, and hides their
 * own studio from them.
 */

/** Roles that mean "this account is scoped to one or more studios". */
export const STUDIO_SCOPED_ROLES = [
  'studio', // the Studio DOCUMENT is the login (70 of these in production)
  'brand_owner',
  'studio_owner',
  'manager',
  'instructor',
];

export function isStudioScopedRole(role?: string | null): boolean {
  return !!role && STUDIO_SCOPED_ROLES.includes(role);
}

/**
 * Owner-level: revenue, analytics, and studio create/edit/delete.
 *
 * `'studio'` is included because that is what a studio owner signing in with
 * the studio's own email has always been called. `studio_owner` and
 * `brand_owner` are the same authority arriving as a named person instead.
 */
export function isOwnerRole(role?: string | null): boolean {
  return role === 'studio' || role === 'studio_owner' || role === 'brand_owner';
}

/** Manager-level or above — everyone studio-scoped except a plain instructor. */
export function isManagerOrAbove(role?: string | null): boolean {
  return isStudioScopedRole(role) && role !== 'instructor';
}

/** Platform staff. */
export function isPlatformAdmin(role?: string | null): boolean {
  return role === 'admin' || role === 'super_admin';
}

/** How a role reads in the UI. */
export const ROLE_LABEL: Record<string, string> = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  studio: 'Studio Owner',
  brand_owner: 'Brand Owner',
  studio_owner: 'Studio Owner',
  manager: 'Manager',
  instructor: 'Instructor',
};
